import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
} from "./ui/select";
import { DropdownMenuItem } from "./ui/dropdown-menu";
import { Box, Typography } from "@mui/material";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Button } from "./ui/button";
import { IconX } from "@tabler/icons-react";
import { User } from "@/interfaces/User";
import { useSelector } from "react-redux";
import { clientSelector } from "@/redux/clients/clients.selector";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { getCompanies } from "@/redux/clients/clients.action";
import { Separator } from "./ui/separator";

import { Client, Subscriptions } from "@/redux/clients/types/Clients.interface";
import { Badge } from "./ui/badge";
import { updateSubscriptions } from "@/redux/user/user.action";
import { allUsers as fetchAllUsers } from "@/redux/user/user.action";
import { SuccessToast } from "@/utilities/toasts/SuccessToast";
import { ErrorToast } from "@/utilities/toasts/ErrorToast";
import { RequestStatus } from "@/utilities/ResponseEnum/RequestStatus";

interface DialogProfileProps {
  userInfo: Partial<User>;
  userId: string;
}

export const DialogSubsEdit = ({ userInfo }: DialogProfileProps) => {
  const [selectedCompanies, setSelectedCompanies] = useState<Client[]>([]);

  const [open, setOpen] = useState(false);
  const { companies, loading } = useSelector(clientSelector).ui;
  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(getCompanies());
  }, [dispatch]);

  useEffect(() => {
    setSelectedCompanies(
      getCompaniesDetailsByFilterId(
        companies || [],
        userInfo.suscripciones || [],
      ),
    );
  }, [companies, userInfo.suscripciones]);

  const getCompaniesDetailsByFilterId = (
    companies: Client[],
    userCompanies: Subscriptions[],
  ) => {
    const userCompaniesDetails = companies.filter((item) => {
      return userCompanies.some((item2) => {
        return item2.cliente.id === item.id;
      });
    });

    return userCompaniesDetails;
  };

  const findCompanyDetails = (companyId: string) => {
    const companyDetails = companies?.find((company) => {
      return company.id.toString() === companyId;
    });
    if (!companyDetails) return null;
    return companyDetails;
  };

  const handleSelectCompany = (companyId: string) => {
    const companyDetails = findCompanyDetails(companyId);
    if (!companyDetails) return;

    setSelectedCompanies((prev) => {
      const alreadyExists = prev.some((c) => c.id === companyDetails.id);
      if (alreadyExists) {
        return prev;
      }
      return [...prev, companyDetails];
    });
  };

  const handleRemoveCompany = (companyId: number) => {
    setSelectedCompanies((prev) =>
      prev.filter((company) => company.id !== companyId),
    );
  };

  const handleUpdateSubscriptions = async () => {
    const listedCompanies = selectedCompanies.map((company) => {
      return {
        id: company.id,
        folders: company.carpetas.map((carpeta) => carpeta.id.toString()),
      };
    });

    if (userInfo?.id) {
      const response = await dispatch(
        updateSubscriptions({
          user: {
            suscripciones: listedCompanies,
          },
          userId: userInfo?.id,
        }),
      );
      if (response.meta.requestStatus === RequestStatus.Fulfilled) {
        SuccessToast({ message: "Subscripciones actualizadas correctamente" });
        setOpen(false);
        await dispatch(fetchAllUsers());
      } else {
        ErrorToast({ message: "Error al actualizar las subscripciones" });
        setOpen(false);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger>
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
          }}
        >
          <Typography fontFamily={"Inter"} fontSize={"14px"} fontWeight={400}>
            Editar subscripciones
          </Typography>
        </DropdownMenuItem>
      </DialogTrigger>
      <VisuallyHidden>
        <DialogTitle>SubscriptionsConfig</DialogTitle>
      </VisuallyHidden>
      <DialogContent
        className="bg-sidebar min-w-[15rem] w-[30rem]"
        aria-describedby={undefined}
      >
        <DialogHeader className="flex pb-0 h-fit">
          <Box className="flex justify-end w-full font-bold">
            <Button
              className="p-0 text-[#1F6B45] h-fit"
              onClick={() => setOpen(false)}
            >
              <IconX className="p-0" />
            </Button>
          </Box>
          <DialogTitle>
            <Typography
              fontFamily={"Inter"}
              fontSize={"18px"}
              fontWeight={500}
              letterSpacing={0}
            >
              Editar subscripciones
            </Typography>
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-500">
            Edita las subscripciones y empresas que puede ver tu usuario.
          </DialogDescription>
        </DialogHeader>
        <Box>
          <Typography fontFamily={"Inter"} fontSize={"14px"} fontWeight={400}>
            Compañias a las que está suscrito el usuario
          </Typography>
          <Box className="flex flex-wrap gap-2 my-2 max-h-[150px] overflow-y-auto">
            {userInfo.role !== "Administrador" ? (
              selectedCompanies.length === 0 ? (
                <Box>
                  <Separator className="bg-gray-200 my-2" />
                  <Typography
                    fontFamily={"Inter"}
                    fontSize={"12px"}
                    fontWeight={400}
                  >
                    No hay compañias a las que está suscrito el usuario
                  </Typography>
                </Box>
              ) : (
                selectedCompanies.map((subscription) => (
                  <Badge
                    key={subscription.id}
                    asChild
                    variant="default"
                    className="shadow-sm hover:bg-[#1F6B45] text-[#1F6B45] hover:text-white"
                  >
                    <div>
                      <Typography
                        fontFamily={"Inter"}
                        fontSize={"10px"}
                        fontWeight={400}
                        key={subscription.id}
                        textTransform={"capitalize"}
                        className="max-w-[100px] truncate"
                      >
                        {subscription.nombre}
                      </Typography>
                      <span className="rounded-full p-0.5 hover:bg-white/30">
                        <IconX
                          className="w-3 h-3 cursor-pointer text-inherit"
                          onClick={() => handleRemoveCompany(subscription.id)}
                        />
                      </span>
                    </div>
                  </Badge>
                ))
              )
            ) : (
              <Typography
                fontFamily={"Inter"}
                fontSize={"12px"}
                fontWeight={400}
              >
                Este usuario está suscrito a todas las compañias
              </Typography>
            )}
          </Box>
          {userInfo.role !== "Administrador" && (
            <Select onValueChange={handleSelectCompany}>
              <SelectTrigger className="w-full">
                <span className="text-sm text-muted-foreground">
                  Añade otra compañía
                </span>
              </SelectTrigger>
              <SelectContent
                position="popper"
                className="max-h-[350px] overflow-y-auto"
              >
                <SelectGroup>
                  <SelectLabel>Compañías</SelectLabel>
                  {companies?.map((company) => {
                    const isSelected = selectedCompanies.some(
                      (sel) => sel.id === company.id,
                    );
                    return (
                      <SelectItem
                        value={company.id.toString()}
                        key={company.id}
                        isSelected={isSelected}
                      >
                        <div className="flex justify-between items-center w-full">
                          <span>{company.nombre}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectGroup>
              </SelectContent>
            </Select>
          )}
        </Box>
        <DialogFooter>
          <Button
            variant="contained"
            className="shadow-sm"
            onClick={handleUpdateSubscriptions}
            disabled={loading}
          >
            Confirmar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
